/**
 * Creates a simple heartbeat sound effect programmatically
 * This is a fallback for when no heartbeat.mp3 file is available
 */

export function createHeartbeatSound(): string {
  // Create a simple heartbeat sound using Web Audio API
  // This returns a data URL that can be used as an audio source
  
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const sampleRate = audioContext.sampleRate;
  const duration = 0.2; // 200ms
  const samples = sampleRate * duration;
  
  // Create buffer
  const buffer = audioContext.createBuffer(1, samples, sampleRate);
  const channelData = buffer.getChannelData(0);
  
  // Generate a simple heartbeat-like sound (two quick thumps)
  for (let i = 0; i < samples; i++) {
    const t = i / sampleRate;
    
    // First thump (0-0.05s)
    if (t < 0.05) {
      const freq = 60; // Low frequency for thump
      const envelope = Math.exp(-t * 20); // Quick decay
      channelData[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.3;
    }
    // Brief pause (0.05-0.08s)
    else if (t < 0.08) {
      channelData[i] = 0;
    }
    // Second thump (0.08-0.13s)
    else if (t < 0.13) {
      const freq = 50; // Slightly lower frequency
      const envelope = Math.exp(-(t - 0.08) * 25); // Quick decay
      channelData[i] = Math.sin(2 * Math.PI * freq * (t - 0.08)) * envelope * 0.2;
    }
    // Silence for the rest
    else {
      channelData[i] = 0;
    }
  }
  
  // Convert buffer to WAV data URL
  return bufferToWav(buffer);
}

function bufferToWav(buffer: AudioBuffer): string {
  const length = buffer.length;
  const numberOfChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const arrayBuffer = new ArrayBuffer(44 + length * 2);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * 2, true);
  
  // Convert float samples to 16-bit PCM
  const channelData = buffer.getChannelData(0);
  let offset = 44;
  for (let i = 0; i < length; i++) {
    const sample = Math.max(-1, Math.min(1, channelData[i]));
    view.setInt16(offset, sample * 0x7FFF, true);
    offset += 2;
  }
  
  // Convert to base64 data URL
  const blob = new Blob([arrayBuffer], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
}
